import React, { useEffect, useState } from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ActivityIndicator, View, StyleSheet, Text, Image } from 'react-native';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import GalaxyHeaderBackground from '../components/GalaxyHeaderBackground';
import SplashScreen from '../screens/SplashScreen';

// Import screens
import LoginScreen from '../screens/LoginScreen';
import MainTabNavigator from './MainTabNavigator';
import NotificationSettingsScreen from '../screens/NotificationSettingsScreen';
import PushDiagnosticsScreen from '../screens/PushDiagnosticsScreen';
import SitesManagementScreen from '../screens/SitesManagementScreen';
import ZonesManagementScreen from '../screens/ZonesManagementScreen';
import KiosksManagementScreen from '../screens/KiosksManagementScreen';
import MeetingRoomScheduleScreen from '../screens/MeetingRoomScheduleScreen';
import ZonePermissionsScreen from '../screens/ZonePermissionsScreen';
import { UsersScreen } from '../screens/UsersScreen';
import { SettingsScreen } from '../screens/SettingsScreen';
import TwoFactorSetupScreen from '../screens/TwoFactorSetupScreen';
import TwoFactorVerifyScreen from '../screens/TwoFactorVerifyScreen';
import TwoFactorSettingsScreen from '../screens/TwoFactorSettingsScreen';
import EmailOtpVerifyScreen from '../screens/EmailOtpVerifyScreen';
import ScanBarcodeScreen from '../screens/ScanBarcodeScreen';
import DetectionResultScreen from '../screens/DetectionResultScreen';
import UnmappedSensorsListScreen from '../screens/UnmappedSensorsListScreen';
import DeviceConfigScreen from '../screens/DeviceConfigScreen';
import ProvisionSuccessScreen from '../screens/ProvisionSuccessScreen';

export type RootStackParamList = {
  Splash: undefined;
  Login: undefined;
  Main: undefined;
  NotificationSettings: undefined;
  PushDiagnostics: undefined;
  SitesManagement: undefined;
  ZonesManagement: undefined;
  KiosksManagement: undefined;
  MeetingRoomSchedule: undefined;
  ZonePermissions: { zoneId: string; zoneName: string };
  UsersManagement: undefined;
  Settings: undefined;
  TwoFactorSetup: undefined;
  TwoFactorVerify: undefined;
  TwoFactorSettings: undefined;
  EmailOtpVerify: undefined;
  ScanBarcode: undefined;
  DetectionResult: { serialNumber: string };
  UnmappedSensorsList: undefined;
  DeviceConfig: {
    serialNumber: string;
    sensor: any;
    detection?: any;
    mode: 'scan' | 'list';
  };
  ProvisionSuccess: {
    device: any;
    mqttPassword: string;
  };
};

const Stack = createStackNavigator<RootStackParamList>();

export default function RootNavigator() {
  const { user, isAuthenticated, isLoading, pending2FA } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [showSplash, setShowSplash] = useState(true); // ✅ Control splash from here

  useEffect(() => {
    logger.log('RootNavigator state:', {
      isLoading,
      isAuthenticated,
      pending2FA,
      hasUser: !!user,
      userId: user?.id,
      orgId: user?.orgId,
      hasOrganization: !!user?.organization,
      showSplash,
    });
  }, [isLoading, isAuthenticated, pending2FA, user, showSplash]);

  // ✅ Hide splash once auth is loaded
  useEffect(() => {
    if (!isLoading) {
      logger.log('🎯 [RootNavigator] Auth loading complete, hiding splash');
      setShowSplash(false);
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <View style={styles.logoWrapper}>
          <Image
            source={require('../../assets/icon.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>
        <ActivityIndicator
          size="large"
          color={theme.colors.primary}
          style={styles.loadingIndicator}
        />
        <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>
          {t('loading') || 'Loading...'}
        </Text>
      </View>
    );
  }

  // ✅ Show splash only on first load
  if (showSplash) {
    return (
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
          headerBackground: () => <GalaxyHeaderBackground />,
          headerTintColor: '#ffffff',
          headerTitleStyle: {
            color: '#ffffff',
            fontWeight: '700',
          },
          headerStyle: {
            backgroundColor: 'transparent',
            elevation: 0,
            shadowOpacity: 0,
          },
        }}
        initialRouteName="Splash"
      >
        {/* Only show splash screen */}
        <Stack.Screen
          name="Splash"
          component={SplashScreen}
          options={{
            headerShown: false,
            gestureEnabled: false,
            cardStyle: { backgroundColor: '#1a1a2e' },
          }}
          listeners={{
            beforeRemove: (e) => {
              logger.log('🎯 [RootNavigator] Splash beforeRemove event');
            },
          }}
        />
      </Stack.Navigator>
    );
  }

  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        headerBackground: () => <GalaxyHeaderBackground />,
        headerTintColor: '#ffffff',
        headerTitleStyle: {
          color: '#ffffff',
          fontWeight: '700',
        },
        headerStyle: {
          backgroundColor: 'transparent',
          elevation: 0,
          shadowOpacity: 0,
        },
      }}
      initialRouteName={isAuthenticated ? 'Main' : 'Login'}
    >
      {/* ✅ Removed Splash - handled above */}

      {pending2FA ? (
        // ✅ 2FA FLOW - Stack منفصل يمنع الرجوع للوجن
        <>
          <Stack.Screen name="TwoFactorVerify" component={TwoFactorVerifyScreen} />
          <Stack.Screen name="EmailOtpVerify" component={EmailOtpVerifyScreen} />
          <Stack.Screen name="Login" component={LoginScreen} />
        </>
      ) : !isAuthenticated ? (
        // ✅ NORMAL LOGIN FLOW
        <>
          <Stack.Screen name="Login" component={LoginScreen} />
          <Stack.Screen
            name="TwoFactorVerify"
            component={TwoFactorVerifyScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen name="EmailOtpVerify" component={EmailOtpVerifyScreen} />
        </>
      ) : (
        <>
          <Stack.Screen name="Main" component={MainTabNavigator} />
          <Stack.Screen
            name="NotificationSettings"
            component={NotificationSettingsScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="PushDiagnostics"
            component={PushDiagnosticsScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="SitesManagement"
            component={SitesManagementScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="ZonesManagement"
            component={ZonesManagementScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="KiosksManagement"
            component={KiosksManagementScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="MeetingRoomSchedule"
            component={MeetingRoomScheduleScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="ZonePermissions"
            component={ZonePermissionsScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="UsersManagement"
            component={UsersScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="Settings"
            component={SettingsScreen}
            options={{ headerShown: true }}
          />
          <Stack.Screen
            name="TwoFactorSetup"
            component={TwoFactorSetupScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="TwoFactorSettings"
            component={TwoFactorSettingsScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="EmailOtpVerify"
            component={EmailOtpVerifyScreen}
            options={{ headerShown: false }}
          />
          <Stack.Screen
            name="ScanBarcode"
            component={ScanBarcodeScreen}
            options={{
              headerShown: true,
              title: t('sensor_provisioning.scan_barcode') || 'Scan Sensor Barcode',
            }}
          />
          <Stack.Screen
            name="DetectionResult"
            component={DetectionResultScreen}
            options={{
              headerShown: true,
              title: t('sensor_provisioning.detection_result') || 'Detection Result',
            }}
          />
          <Stack.Screen
            name="UnmappedSensorsList"
            component={UnmappedSensorsListScreen}
            options={{
              headerShown: true,
              title: t('sensor_provisioning.unmapped_sensors') || 'Unmapped Sensors',
            }}
          />
          <Stack.Screen
            name="DeviceConfig"
            component={DeviceConfigScreen}
            options={{
              headerShown: true,
              title: t('sensor_provisioning.device_config') || 'Device Configuration',
            }}
          />
          <Stack.Screen
            name="ProvisionSuccess"
            component={ProvisionSuccessScreen}
            options={{
              headerShown: false,
              gestureEnabled: false,
            }}
          />
        </>
      )}
    </Stack.Navigator>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoWrapper: {
    marginBottom: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 180,
    height: 180,
  },
  loadingIndicator: {
    marginTop: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: '600',
  },
});
